import type { DiscoveredKind, ExclusionReason } from "./types";

/**
 * Path segments that name a credential. The guard test (`src/ui/__tests__/aiFormExclusionGuard.test.ts`)
 * keeps its own copy of this pattern so a page cannot pass the test with a field the bridge would
 * still send — the two must stay identical; change both or neither.
 */
export const SENSITIVE_PATH_PATTERN = /(password|secret|token|apikey|accesskey|privatekey|otp|mfa|hmac|salt|signature|credential)/i;

// The path charset and length admin-mcp accepts — keep them in sync with PageFormFormatter. A field
// outside them would be dropped by the server.
const WIRE_PATH_CHARACTER_CLASS = "A-Za-z0-9_.[\\]-";
export const MAX_WIRE_PATH_LENGTH = 64;
export const WIRE_PATH_PATTERN = new RegExp(`^[${WIRE_PATH_CHARACTER_CLASS}]{1,${MAX_WIRE_PATH_LENGTH}}$`);
/** Each run of characters a wire path cannot hold. */
export const WIRE_PATH_FOREIGN_CHARACTERS = new RegExp(`[^${WIRE_PATH_CHARACTER_CLASS}]+`, "g");

const EXCLUDE_REASONS: ReadonlySet<string> = new Set(["secret", "personal", "config"]);

function markedReason(scope: HTMLElement): ExclusionReason | undefined {
  const marked = scope.closest("[data-ai-exclude]");
  if (!marked) {
    return undefined;
  }
  const value = marked.getAttribute("data-ai-exclude") ?? "";
  // A bare or unknown mark still protects: the safest reason wins over a typo.
  return EXCLUDE_REASONS.has(value) ? (value as ExclusionReason) : "secret";
}

function isPasswordInput(element: HTMLElement | undefined): boolean {
  return element instanceof HTMLInputElement && element.type.toLowerCase() === "password";
}

function hasSensitiveSegment(path: string): boolean {
  return path.split(/[.[\]]+/).some((segment) => SENSITIVE_PATH_PATTERN.test(segment));
}

/** A map excluded as a whole: every field under its model is excluded too, whatever renders it. */
export interface ExcludedScope {
  model: string;
  reason: ExclusionReason;
}

/** The scope a `FormMap` (its `data-ai-map` add-row) covers when the map sits under an exclusion mark. */
export function excludedScopeOf(mapElement: HTMLElement): ExcludedScope | undefined {
  const model = mapElement.getAttribute("data-ai-map");
  const reason = markedReason(mapElement);
  return model && reason ? { model, reason } : undefined;
}

function scopeReason(path: string, scopes: readonly ExcludedScope[]): ExclusionReason | undefined {
  const covering = scopes.find(({ model }) => path === model || path.startsWith(`${model}.`) || path.startsWith(`${model}[`));
  return covering?.reason;
}

export interface ExclusionInput {
  /** The field's element; a registry handle without one is judged by the form it belongs to. */
  element: HTMLElement | undefined;
  form: HTMLFormElement;
  /** The field's full path as the page binds it — before it is fitted to the wire charset. */
  path: string;
  kind: DiscoveredKind;
  /** False for a kit handle bound to a fieldSet of its own (a map add-row) rather than the page form. */
  pageLevel: boolean;
  /** Excluded maps anywhere on the page. */
  excludedScopes: readonly ExcludedScope[];
}

/** Why a field must be listed without its value, or undefined when it may be sent. First match wins. */
export function exclusionFor(input: ExclusionInput): ExclusionReason | undefined {
  const marked = markedReason(input.element ?? input.form);
  if (marked) {
    return marked;
  }
  const scoped = scopeReason(input.path, input.excludedScopes);
  if (scoped) {
    return scoped;
  }
  if (isPasswordInput(input.element)) {
    return "secret";
  }
  if (hasSensitiveSegment(input.path)) {
    return "auto-sensitive";
  }
  if (!WIRE_PATH_PATTERN.test(input.path) || !input.pageLevel || input.kind === "unsupported") {
    return "unsupported";
  }
  return undefined;
}
